import { endOfDay, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import type {
  TransactionStatus,
  TransactionDirection,
} from "Infrastructure/Api/Api";
import type {
  TimePeriod,
  TransactionFilters,
} from "Components/Transactions/hooks/useTransactionFilterManager";

export type TransactionFilterParams = {
  searchPublicID?: string;
  dateFrom?: string;
  dateTo?: string;
  payPointPublicID?: string;
  employeePublicID?: string;
  status?: TransactionStatus;
  direction?: TransactionDirection;
  isRefundOrCancellation?: boolean;
};

type DateRange = { dateFrom: Date; dateTo: Date };

export const getDateRangeForPeriod = (period: TimePeriod): DateRange => {
  const now = new Date();
  const dateTo = endOfDay(now);

  const lookupMap: Record<TimePeriod, DateRange> = {
    day: { dateFrom: startOfDay(now), dateTo },
    week: { dateFrom: startOfWeek(now, { weekStartsOn: 1 }), dateTo },
    month: { dateFrom: startOfMonth(now), dateTo },
    custom: { dateFrom: startOfMonth(now), dateTo },
  };

  return lookupMap[period];
};

export const determineDatesByPeriod = ({
  timePeriod,
  dateFrom,
  dateTo,
}: {
  timePeriod?: TimePeriod;
  dateFrom?: Date;
  dateTo?: Date;
}): { dateFrom?: string; dateTo?: string } => {
  // If no period is set, don't apply date filter (include all transactions)
  if (!timePeriod) {
    return { dateFrom: undefined, dateTo: undefined };
  }

  if (timePeriod === "custom") {
    return {
      dateFrom: dateFrom ? startOfDay(dateFrom).toISOString() : undefined,
      dateTo: dateTo ? endOfDay(dateTo).toISOString() : undefined,
    };
  }

  const range = getDateRangeForPeriod(timePeriod);
  return {
    dateFrom: range.dateFrom.toISOString(),
    dateTo: range.dateTo.toISOString(),
  };
};

export const filtersToQueryParams = (
  filters: Partial<TransactionFilters> | undefined,
): TransactionFilterParams => {
  const { dateFrom, dateTo } = determineDatesByPeriod(filters ?? {});

  return {
    searchPublicID: filters?.searchPublicID,
    dateTo,
    dateFrom,
    status: filters?.status,
    payPointPublicID: filters?.payPoint?.publicID,
    employeePublicID: filters?.employee?.publicID,
    direction: filters?.direction,
    isRefundOrCancellation: filters?.isRefundOrCancellation,
  };
};
